import tinycolor from 'tinycolor2';
import {
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  BOOLEAN,
  CAN_SCAN,
  COLUMN_GRAPH,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  IMAGE,
  LINE_GRAPH,
  LOCATION,
  MAINTENANCE,
  MAP,
  PIE,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
  VERTICAL_COLUMN_GRAPH,
} from '../Constants/WidgetTypes';

export default class CreateWidgetHelper {
  constructor(type) {
    this.type = type;
    this.settings = {};
    this.sizeX = 1;
    this.sizeY = 1;
    this.row = 0;
    this.col = 0;
  }
  addSettingsByType({
    displayTextAsGrid,
    image,
    imageCoordinatesMap,
    maintenanceSelectValue,
    selectedAggregation,
    selectedSignals,
    showTimeSinceLastUpdated,
    signalCollections,
    title,
    yMin,
    yMax,
  }) {
    switch (this.type) {
      case BAR_ENGINE_UTILIZATION:
      case BAR_FUEL_UTILIZATION:
        return this.addBarUtilizationSettings(title, selectedAggregation);
      case COLUMN_GRAPH:
      case VERTICAL_COLUMN_GRAPH:
        return this.addBarChartSettings(title, selectedSignals, yMin, yMax);
      case LOCATION:
      case MAP:
        return this.addMapSettings(title, signalCollections);
      case GAUGE:
        return this.addGaugeSettings(title, selectedSignals, signalCollections);
      case GAUGE_RANGE:
        return this.addGaugeRangeSettings(
          title,
          selectedSignals,
          signalCollections
        );
      case AREA:
      case LINE_GRAPH:
        return this.addLineGraphSettings(title, selectedSignals, yMin, yMax);
      case TEXT:
        return this.addTextSettings(
          title,
          selectedSignals,
          showTimeSinceLastUpdated,
          displayTextAsGrid
        );
      case TEXT_ENGINE:
      case TEXT_FUEL:
        return this.addUtilizationSettings(title);
      case TEXT_STATIC:
        return this.addTextStaticSettings(title, selectedSignals);
      case TEXT_TAGS:
        return this.addTextTagsSettings(title, selectedSignals);
      case CAN_SCAN:
        return this.addScanSettings(title, selectedSignals);
      case BOOLEAN:
        return this.addBooleanSettings(title, selectedSignals);
      case PIE:
        return this.addPieChartSettings(title, selectedSignals);
      case COMMAND:
        return this.addCommandSettings(title, selectedSignals);
      case IMAGE:
        return this.addImageOverlaySettings(title, image, imageCoordinatesMap);
      case MAINTENANCE:
        return this.addMaintenanceSettings(title, maintenanceSelectValue);
    }
  }
  addBarChartSettings(title, selectedSignals, yMin, yMax) {
    const conditionalMaxMin = {};
    if (Number.isFinite(yMax)) conditionalMaxMin.yMax = yMax;
    if (Number.isFinite(yMin)) conditionalMaxMin.yMin = yMin;

    const columns = this.transformSelectedSignals(selectedSignals);
    this.settings = { columns, title, ...conditionalMaxMin };
    return this;
  }
  addMapSettings(title, signalCollections) {
    const gpsDP = signalCollections.find(({ type = '' }) => {
      return type.toLowerCase() === 'gps';
    });

    this.settings = {
      title,
      latitude: 'latitude',
      longitude: 'longitude',
      datapointID: gpsDP._id,
      datapoint: gpsDP,
    };
    return this;
  }
  addGaugeSettings(title, selectedSignals, signalCollections) {
    const {
      datapoint: { value: datapointID },
      maxValue,
      minValue,
      signal: { label: key, value: outputSignal },
    } = selectedSignals[0];

    let conditionalType = {};
    let datapoint;
    if (datapointID === 'outputSignal') {
      conditionalType = { type: datapointID };
      datapoint = outputSignal;
    } else {
      datapoint = signalCollections.find(({ _id }) => _id === datapointID);
    }

    const max = Number.parseFloat(maxValue);
    const min = Number.parseFloat(minValue);
    if (Number.isNaN(max) || Number.isNaN(min)) {
      throw new Error('Min and Max must be configured.');
    }
    if (max <= min) throw new Error('Max value must be greater than min.');

    let optionalSecond = {};
    if (selectedSignals[1]?.signal?.value) {
      const {
        datapoint: { value: selectedDP2 },
        maxValue: maxValue2,
        minValue: minValue2,
        signal,
      } = selectedSignals[1];

      let conditionalType2 = {};
      let datapoint2;
      if (selectedDP2 === 'outputSignal') {
        conditionalType2 = { type: selectedDP2 };
        datapoint2 = signal?.value;
      } else {
        datapoint2 = signalCollections.find(({ _id }) => _id === selectedDP2);
      }

      const max2 = Number.parseFloat(maxValue2);
      const min2 = Number.parseFloat(minValue2);
      if (Number.isNaN(max2) || Number.isNaN(min2)) {
        throw new Error('Min and Max must be configured.');
      }
      if (max2 <= min2) throw new Error('Max value must be greater than min.');

      optionalSecond = {
        secondGauge: {
          ...conditionalType2,
          datapoint: datapoint2,
          datapointID: datapoint2?._id,
          key: signal.label,
          max: max2,
          min: min2,
        },
      };
    }

    this.settings = {
      ...conditionalType,
      datapoint,
      datapointID: datapoint._id,
      key,
      max,
      min,
      ...optionalSecond,
      title,
    };
    return this;
  }
  addGaugeRangeSettings(title, selectedSignals, signalCollections) {
    const {
      datapoint: { value: selectedDP },
      ranges,
      signal: { label: key, value: outputSignal },
    } = selectedSignals[0];

    let conditionalType = {};
    let datapoint;
    if (selectedDP === 'outputSignal') {
      conditionalType = { type: selectedDP };
      datapoint = outputSignal;
    } else {
      datapoint = signalCollections.find(({ _id }) => _id === selectedDP);
    }

    const firstWidgetRanges = ranges.map(
      ({ color, label, maxValue, minValue }) => {
        validateWidgetRanges({ color, label, maxValue, minValue });
        const max = Number.parseFloat(maxValue);
        const min = Number.parseFloat(minValue);
        return { color, label, max, min };
      }
    );
    let optionalSecond = {};
    if (selectedSignals[1]?.signal?.value) {
      const {
        datapoint: { value: selectedDP2 },
        ranges: ranges2,
        signal,
      } = selectedSignals[1];

      let conditionalType2 = {};
      let datapoint2;
      if (selectedDP2 === 'outputSignal') {
        conditionalType2 = { type: selectedDP2 };
        datapoint2 = signal?.value;
      } else {
        datapoint2 = signalCollections.find(({ _id }) => _id === selectedDP2);
      }

      const secondWidgetRanges = ranges2.map(
        ({ color, label, maxValue, minValue }) => {
          validateWidgetRanges({ color, label, maxValue, minValue });
          const max = Number.parseFloat(maxValue);
          const min = Number.parseFloat(minValue);
          return { color, label, max, min };
        }
      );
      optionalSecond = {
        secondGauge: {
          ...conditionalType2,
          datapoint: datapoint2,
          datapointID: datapoint2?._id,
          key: signal.label,
          ranges: secondWidgetRanges,
        },
      };
    }
    this.settings = {
      ...conditionalType,
      datapoint,
      datapointID: datapoint._id,
      key,
      ranges: firstWidgetRanges,
      ...optionalSecond,
      title,
    };
    return this;
  }
  addImageOverlaySettings(title, image, imageCoordinatesMap) {
    const lines = this.transformImageCoordinatesMap(imageCoordinatesMap);
    this.settings = { image, lines, title };
    return this;
  }
  addLineGraphSettings(title, selectedSignals, yMin, yMax) {
    const conditionalMaxMin = {};
    if (Number.isFinite(yMax)) conditionalMaxMin.yMax = yMax;
    if (Number.isFinite(yMin)) conditionalMaxMin.yMin = yMin;

    const lines = this.transformSelectedSignals(selectedSignals);
    this.settings = { title, lines, ...conditionalMaxMin };
    return this;
  }
  addMaintenanceSettings(title, maintenanceSelectValue) {
    const maintenance = maintenanceSelectValue.map(({ label, value }) => ({
      reminder_id: value,
      reminder_name: label,
    }));
    this.settings = { maintenance, title };
    return this;
  }
  addTextSettings(
    title,
    selectedSignals,
    showTimeSinceLastUpdated,
    displayTextAsGrid
  ) {
    const text = this.transformSelectedSignals(selectedSignals);
    this.settings = {
      displayTextAsGrid,
      showTimeSinceLastUpdated,
      title,
      text,
    };
    return this;
  }
  addTextStaticSettings(title, selectedSignals) {
    const [{ links: inputLinks, value: staticText }] = selectedSignals;
    if (!staticText || staticText.length > 512) {
      throw new Error('Text required. Maximum characters allowed is 512');
    }

    const links = inputLinks?.reduce?.(
      (acc, l) => (l?.display && l?.url ? acc.concat(l) : acc),
      []
    );
    this.settings = { links, staticText, title };
    return this;
  }
  addTextTagsSettings(title, selectedTags) {
    const tags = selectedTags.map(({ value }) => value).filter((t) => !!t);
    this.settings = { title, tags };
    return this;
  }
  addScanSettings(title, selectedSignals) {
    const scan = this.transformSelectedSignals(selectedSignals);
    this.settings = { title, scan };
    return this;
  }
  addBooleanSettings(title, selectedSignals) {
    const boolean = this.transformSelectedSignals(selectedSignals);
    this.settings = { title, boolean };
    return this;
  }
  addPieChartSettings(title, selectedSignals) {
    const lines = this.transformSelectedSignals(selectedSignals);
    this.settings = { title, lines };
    return this;
  }
  addCommandSettings(title, selectedSignals) {
    const commands = selectedSignals.map((c) => {
      const { color, command: { value } = {}, friendlyName } = c || {};
      if (!color || !value || !friendlyName) {
        throw new Error(
          'Each row must have a command, friendly name and color selected.'
        );
      }
      return { color, commandID: value, friendlyName };
    });
    this.settings = { title, commands };
    return this;
  }
  addBarUtilizationSettings(title, aggregation) {
    this.settings = { aggregation, title };
    return this;
  }
  addUtilizationSettings(title) {
    this.settings = { title };
    return this;
  }
  transformImageCoordinatesMap(imageCoordinatesMap) {
    if (!imageCoordinatesMap?.size) {
      throw new Error('Tile must have at least one image overlay configured.');
    }

    return [...imageCoordinatesMap.values()].map(
      ({ datapoint, signal, x, y }) => {
        if (!datapoint?.value) {
          throw new Error('Smart Tile must have datapoint selected!');
        } else if (!signal?.label) {
          throw new Error('Smart Tile must have signal selected');
        }

        let conditionalType = {};
        let datapointID = datapoint.value;
        if (datapoint.value === 'outputSignal') {
          conditionalType = { type: datapoint.value };
          if (!signal?.value?._id) {
            throw new Error('Smart Tile must have signal selected');
          }
          datapointID = signal.value._id;
        }
        return { ...conditionalType, datapointID, key: signal.label, x, y };
      }
    );
  }
  transformSelectedSignals(selectedSignals) {
    return selectedSignals.map(({ datapoint, signal }) => {
      if (!datapoint?.value) {
        throw new Error('Smart Tile must have datapoint selected!');
      } else if (!signal?.label) {
        throw new Error('Smart Tile must have signal selected');
      }

      let conditionalType = {};
      let datapointID = datapoint.value;
      if (datapoint.value === 'outputSignal') {
        conditionalType = { type: datapoint.value };
        if (!signal?.value?._id) {
          throw new Error('Smart Tile must have signal selected');
        }
        datapointID = signal.value._id;
      }
      return { ...conditionalType, datapointID, key: signal.label };
    });
  }
}

function validateWidgetRanges({ color, label, maxValue, minValue }) {
  if (!tinycolor(color).isValid()) {
    throw new Error('Valid hex color must be configured for each range.');
  }
  if (!label) throw new Error('Label must be configured for each range.');
  if (Number.isNaN(Number.parseFloat(maxValue))) {
    throw new Error(
      'Max value for ranges must be a valid floating point number.'
    );
  }
  if (Number.isNaN(Number.parseFloat(minValue))) {
    throw new Error(
      'Min value for ranges must be a valid floating point number.'
    );
  }
  if (Number.parseFloat(maxValue) < Number.parseFloat(minValue)) {
    throw new Error('Max value must be greater than Min value for each range.');
  }
  if (Number.parseFloat(minValue) > Number.parseFloat(maxValue)) {
    throw new Error('Min value must be less than Max value for each range.');
  }
}
